'use client';
import { useState, useEffect, useCallback } from 'react';
import { MapPin, Plus } from 'lucide-react';
import ArrowUpOnSquareIcon from '@heroicons/react/24/outline/ArrowUpOnSquareIcon';
import { useToast } from '@/context/toast-context';
import { useInView } from 'react-intersection-observer';
import { useFeedLikeEvent } from '@/hooks/use-feed-like-event';
import { useFeedCommentEvent } from '@/hooks/use-feed-comment-event';
import { usePlaceLikeEvent } from '@/hooks/use-place-like-event';
import { usePlaceLike } from '../hooks/usePlaceLike';
import { useHeader } from '@/components/complex/header';
import { useBottomNavigation } from '@/components/complex/bottom-navigation';
import api from '@/api';
import NaverMap from '@/components/common/naver-map';
import SkeletonPlaceDetail from '@/components/complex/skeleton-place-detail';
import { PlaceDto } from '@/api/place';
import Typography from '@/components/primitives/typography';
import LikeButton from '@/screen/feed/view/components/like-button';
import FeedItems from '@/components/complex/feed-items';
import Button from '@/components/primitives/button';
import { useRouter } from 'next/navigation';
import styles from './styles.module.scss';
import { useAsyncEffect } from '@/hooks/use-async-effect';

interface Props {
  place: PlaceDto;
}

/**
 * Place detail screen component
 */
const PlaceDetailScreen = (props: Props) => {
  const { place: placeData } = props;
  const [place, setPlace] = useState<PlaceDto | null>(placeData);
  const router = useRouter();

  // Listen for place like events
  usePlaceLikeEvent(setPlace);

  // Place like functionality
  const { handleLike } = usePlaceLike(place);
  const toast = useToast();

  // Share count state and handler
  const [shareCount, setShareCount] = useState(place?.shareCount || 0);

  const handleShare = async () => {
    if (!place) return;
    try {
      const { linkId } = await api.shortLink.create('PLACE', String(place.id));
      const shareUrl = `${window.location.origin}/l/${linkId}`;
      setShareCount(prev => prev + 1);
      if (navigator.share) {
        await navigator.share({ title: place.name, url: shareUrl });
      } else {
        await navigator.clipboard.writeText(shareUrl);
        toast.showToast('링크가 클립보드에 복사되었습니다.');
      }
    } catch (error) {
      if ((error as Error).name !== 'AbortError') {
        toast.showToast('링크 공유에 실패했습니다.');
      }
    }
  };

  // 헤더 설정: 버튼 가시성에 따라 헤더 우측 버튼 노출
  const headerRight = useCallback(() => (
    <Plus width={24} height={24} strokeWidth={1} onClick={() => router.push(`/feeds/write?placeId=${placeData.id}`)} />
  ), [router, placeData?.id]);
  const { ref: writeButtonRef, inView: writeButtonInView } = useInView({ threshold: 0 });
  const showHeaderWrite = !writeButtonInView; // 버튼이 안 보이면 헤더에 표시

  useHeader({
    useBack: true,
    title: place?.name ? `${place.name}` : '장소',
    ...(showHeaderWrite ? { rightElement: headerRight } : {}),
  });

  // 하단 네비게이션 표시
  useBottomNavigation(true);

  // 장소 정보와 관련 피드 가져오기
  const fetchFeeds = async () => {
    try {
      // 좋아요 상태 확인
      try {
        const likeStatus = await api.placeLike.exists(placeData.id);
        setPlace(old => (old ? {...old, isLike: likeStatus.isExist} : null));
      } catch (likeErr) {
        console.error('좋아요 상태를 가져오는데 실패했습니다:', likeErr);
        setPlace(old => (old ? {...old, isLike: false} : null));
      }
    } catch (err) {
      console.error('장소 정보를 가져오는데 실패했습니다:', err);
    }
  };

  // 컴포넌트 마운트 시 데이터 가져오기
  useAsyncEffect(async () => {
    setPlace(placeData);
    setShareCount(placeData?.shareCount || 0);
    await fetchFeeds();
  }, [placeData?.id]);

  // 장소 정보가 없을 때 표시할 UI
  if (!place) {
    return (
      <div className={styles.notFoundContainer}>
        <MapPin width={48} height={48} strokeWidth={1.25} className={styles.notFoundIcon} />
        <Typography className={styles.notFoundTitle}>장소를 찾을 수 없습니다</Typography>
        <Typography className={styles.notFoundSubtext}>
          요청하신 장소 정보가 존재하지 않습니다.
        </Typography>
      </div>
    );
  }

  return (
    <div className={styles.container}>
      {/* 장소 정보 */}
      <div className={styles.placeInfoContainer}>
        <div className={styles.headerRow}>
          <div className={styles.nameWrapper}>
            <h1 className={styles.placeName} style={{ lineHeight: 1 }}>{place.name}</h1>
          </div>
          <div className={styles.actionWrapper}>
            <div className={styles.shareWrapper}>
              <div className="cursor-pointer" onClick={handleShare}>
                <ArrowUpOnSquareIcon width={24} height={24} strokeWidth={1.5} />
              </div>
              <Typography className={styles.actionCount}>{shareCount}</Typography>
            </div>
            <div className={styles.likeWrapper}>
              <LikeButton isLiked={place.isLike || false} onChange={handleLike} />
              <Typography className={styles.actionCount}>{place.likeCount || 0}</Typography>
            </div>
          </div>
        </div>

        {place.caption && (
          <Typography className={styles.caption}>{place.caption}</Typography>
        )}

        {/*{place.jiBunAddress && (*/}
        {/*  <Typography className="text-gray-500 text-sm">*/}
        {/*    {place.jiBunAddress.address} {place.jiBunAddress.detail}*/}
        {/*  </Typography>*/}
        {/*)}*/}

        {place.streetAddress && (
          <Typography className={styles.address}>
            {place.streetAddress.address} {place.streetAddress.detail}
          </Typography>
        )}

        {/* 지도 표시 */}
        {place?.location && place?.location?.lat && place?.location?.lon && (
          <div className={styles.mapContainer}>
            <NaverMap
              lat={place.location.lat}
              lng={place.location.lon}
              height={250}
              className={styles.map}
            />
            <Button
              ref={writeButtonRef}
              className={styles.writeButton}
              onClick={() => router.push(`/feeds/write?placeId=${placeData.id}`)}
            >
              이 장소로 피드 쓰기
            </Button>
          </div>
        )}
      </div>

      {/* 관련 피드 */}
      <div className={styles.feedContainer}>
        <FeedItems
          className={styles.feedGrid}
          link={{
            placeIds: [placeData.id],
          }}
          sortType={'LATEST'}
          onEmptyResult={() => (
            <div className={styles.empty}>
              <Typography align="center" size="small">
                이 장소와 관련된 피드가 아직 없어요
              </Typography>
            </div>
          )}
        />
      </div>
    </div>
  );
};

export default PlaceDetailScreen;
